import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { Button } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import Image from '@/app/common/components/Image/Image'
import { AddToCartIcon, BackIcon } from '@/app/common/icons/general'
import { currencyFormatter } from '@/app/common/utilities'
import HtmlViewer from '@/common/components/HtmlViewer.tsx'
import { CheckMarkIcon } from '@/common/icons/arrows.tsx'

interface ItemDetailUIProps {
  data: MarketplaceItem
  onGoBack: () => void
  onAddToCart: (item: MarketplaceItem) => void
  buttonAnimated: boolean
}

const ItemDetailUI = ({
  data,
  onGoBack,
  onAddToCart,
  buttonAnimated,
}: ItemDetailUIProps) => {
  return (
    <Card>
      <CardHeader className="flex justify-between items-center text-md font-semibold">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoBack}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <BackIcon />
            </button>
            <div>{data?.name}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="">
          <div className="max-w-5xl mx-auto bg-white ">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left: Product Image */}
              <div className="w-full md:w-1/4">
                {data?.image ? (
                  <Image
                    className="w-48 h-48 object-cover border border-gray-300 rounded-md"
                    path={data.image}
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-200 rounded-md flex items-center justify-center text-sm text-gray-500">
                    No image
                  </div>
                )}
              </div>

              <div className="w-full md:w-3/4">
                <h1 className="text-2xl font-semibold">{data?.name}</h1>
                <p className="text-gray-500">
                  Sold by:{' '}
                  <span className="font-semibold text-primary">
                    {data?.provider.name ?? 'Unknown'}
                  </span>
                </p>
                <div className="mt-2 text-xl font-bold text-gray-900">
                  {currencyFormatter.format(data?.price)}
                </div>

                <div className="mt-6">
                  <h2 className="text-lg font-semibold">About this item</h2>
                  <ul className="mt-2 text-sm text-gray-700 list-disc list-inside">
                    <li>
                      {data?.category.name}, {data?.item.name}
                    </li>
                    <li>{data?.keyword_tags}</li>
                    {data?.comments && (
                      <div>
                        <HtmlViewer html={data.comments} />
                      </div>
                    )}
                  </ul>
                </div>
                <div className="relative inline-block">
                  <Button
                    onClick={() => onAddToCart(data)}
                    className={`mt-4 px-6 py-2 text-white font-bold rounded-lg flex items-center justify-center 
                               transition-all duration-300 transform ${
                                 buttonAnimated ? 'scale-105' : 'bg-primary'
                               }`}
                  >
                    {buttonAnimated ? (
                      <CheckMarkIcon className="mr-2 animate-scale-in text-black font-bold text-lg animate-[scaleIn_0.3s_ease-out]" />
                    ) : (
                      <AddToCartIcon className="w-5 h-5 mr-2" />
                    )}
                    Add to Cart
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export { ItemDetailUI }
